import { FC, useEffect, useMemo } from 'react';
import { ModalContainer, ModalHeader } from '@/utils/modal';
import { SampleDefinitionFormData, SampleDefinitionIssue, SampleDefinitionIssueAction } from '@/model/SamplePlan.model';
import { BulkIssueAction, SampleDefinitionIssueCard } from './SampleDefinitionIssueCard';
import styles from './SampleDefinitionIssuesModal.module.scss';
import { _isNil, _notNil } from '@/littledash';

interface SampleDefinitionIssuesModalProps {
  issues: Map<SampleDefinitionFormData['api_id'], Array<SampleDefinitionIssue>>;
  sampleDefinitions: Array<SampleDefinitionFormData>;
  onAction: (action: SampleDefinitionIssueAction) => void;
  onClose: () => void;
}

interface GroupedIssue {
  issue: SampleDefinitionIssue;
  affectedDefinitions: Array<SampleDefinitionFormData>;
}

const getIssueKey = (issue: SampleDefinitionIssue): string => {
  switch (issue.type) {
    case 'archived-sample-type':
      return `archived-sample-type:${issue.sample_type_id}`;
    case 'missing-sample-type':
      return `missing-sample-type:${issue.sample_type_id}`;
    case 'invalid-sample-detail-type':
      return `invalid-sample-detail-type:${[...issue.invalid_detail_types].sort().join(',')}:${issue.sample_type_name}`;
    case 'inactive-metadata':
      return `inactive-metadata:${[...issue.inactive_metadata_ids].sort().join(',')}`;
    case 'missing-metadata':
      return `missing-metadata:${[...issue.missing_metadata_ids].sort().join(',')}`;
    case 'invalid-metadata-value':
      return `invalid-metadata-value:${issue.metadata_id}:${issue.stored_value}`;
    case 'missing-required-metadata':
      return `missing-required-metadata:${issue.missing_required_metadata
        .map((m) => m.metadata_id)
        .sort()
        .join(',')}`;
    case 'invalid-target':
      if (issue.target.type === 'groups') {
        return `invalid-target:groups:${[...issue.target.missing_group_ids].sort().join(',')}`;
      } else if (issue.target.type === 'registered_animals') {
        return `invalid-target:registered_animals:${[...issue.target.missing_animal_ids].sort().join(',')}`;
      } else {
        return `invalid-target:planned_animals:${[...issue.target.missing_animal_ids].sort().join(',')}`;
      }
    default:
      return `unknown:${(issue as any).type}`;
  }
};

export const SampleDefinitionIssuesModal: FC<SampleDefinitionIssuesModalProps> = ({
  issues,
  sampleDefinitions,
  onAction,
  onClose,
}) => {
  useEffect(() => {
    if (issues.size === 0) {
      onClose();
    }
  }, [issues, onClose]);

  const definitionsMap = useMemo(() => {
    return sampleDefinitions.reduce<Map<string, SampleDefinitionFormData>>((map, definition) => {
      map.set(definition.api_id, definition);
      return map;
    }, new Map());
  }, [sampleDefinitions]);

  const groupedIssues = useMemo(() => {
    const groups = new Map<string, GroupedIssue>();

    issues.forEach((definitionIssues, definitionId) => {
      const definition = definitionsMap.get(definitionId);
      if (_isNil(definition)) {
        return;
      }

      definitionIssues.forEach((issue) => {
        const key = getIssueKey(issue);
        const existing = groups.get(key);

        if (_notNil(existing)) {
          existing.affectedDefinitions.push(definition);
        } else {
          groups.set(key, {
            issue,
            affectedDefinitions: [definition],
          });
        }
      });
    });

    return Array.from(groups.values());
  }, [issues, definitionsMap]);

  const totalIssueCount = Array.from(issues.values()).reduce((sum, issueList) => sum + issueList.length, 0);

  const handleActionForGroup = (action: BulkIssueAction): void => {
    if (action.type === 'open-sample-plan-modal') {
      const firstDefinitionId = Array.from(issues.keys())[0];
      if (_notNil(firstDefinitionId)) {
        onAction({
          type: 'change-sample-type',
          definition_id: firstDefinitionId,
        });
      }
    } else {
      // All other BulkIssueAction types are directly compatible with SampleDefinitionIssueAction
      onAction(action);
    }
  };

  return (
    <ModalContainer size="medium">
      <ModalHeader
        title={`Resolve ${totalIssueCount} sample definition issue${totalIssueCount !== 1 ? 's' : ''}`}
        subText="Sample definitions have become invalid and need attention"
        closeModal={onClose}
        className="pa3 bb b--moon-gray bg-white"
      />

      <div className={`pa3 w-100 bl b--moon-gray h-100 overflow-y-auto ${styles.issuesContainer}`}>
        {groupedIssues.map((groupedIssue, idx) => (
          <div key={`${getIssueKey(groupedIssue.issue)}-${idx}`} className="mb4">
            <div className="mb3">
              <SampleDefinitionIssueCard
                issue={groupedIssue.issue}
                affectedDefinitions={groupedIssue.affectedDefinitions}
                onAction={handleActionForGroup}
              />
            </div>
          </div>
        ))}
      </div>
    </ModalContainer>
  );
};
